import {useCallback, useEffect, useState} from "react";
import baseUrl from "../../../baseUrl";
import {requester} from "../../../services/requester";

// Български етикети на действията (D1_AUDIT_LOG_DESIGN §5.4).
export const ACTION_LABELS = {
    login: 'вход',
    login_failed: 'неуспешен вход',
    user_create: 'нов потребител',
    user_update: 'промяна на потребител',
    user_password: 'смяна на парола',
    user_settlements: 'права по землища',
    question_create: 'нов въпрос',
    question_answer: 'отговор на въпрос',
    question_status: 'статус на въпрос',
    question_delete: 'изтрит въпрос',
    initial_info_create: 'нови изходни данни',
    initial_info_edit: 'редакция на данни',
    initial_info_delete: 'изтрит ред данни',
    initial_info_bulk_delete: 'изтриване на данни',
    deal_create: 'нова замяна',
    deal_edit: 'редакция на замяна',
    deal_delete: 'изтрита замяна',
    deal_approve: 'одобрена замяна',
    deal_reject: 'отказана замяна',
    deal_delete_far: 'замяна изтрита отсреща',
    deal_ack: 'замяна отбелязана като уредена',
    protocol_pdf: 'протокол PDF',
    drawing_create: 'ново изчертаване',
    drawing_edit: 'редакция изчертаване',
    drawing_delete: 'изтрито изчертаване',
    others_create: 'нови други площи',
    others_edit: 'редакция други площи',
    copy_year: 'копиране от година',
    company_create: 'нов ползвател (фирма)',
    company_edit: 'редакция на ползвател',
    declarations_upload: 'качване декларации',
    declarations_delete: 'изтрито сравнение декларации',
    declarations_resolution: 'решение по разделен имот',
    crop_create: 'нова култура',
    crop_edit: 'преименувана култура',
    settlement_active: 'активно землище',
    settlement_active_bulk: 'активни землища (групово)',
    public_link_create: 'нов публичен линк',
    public_link_regenerate: 'нов токен на линк',
    public_link_toggle: 'вкл./изкл. линк',
    public_link_delete: 'изтрит публичен линк',
    settlement_doc_upload: 'качен PDF по землище',
    settlement_doc_delete: 'изтрит PDF по землище',
};

const label = (a) => ACTION_LABELS[a] || a;

export const AdminActivity = () => {
    const request = requester();
    const [rows, setRows] = useState([]);
    const [nextBefore, setNextBefore] = useState(null);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({username: '', action: '', settlement_ekatte: '', year: ''});

    const buildQuery = (beforeId) => {
        const p = new URLSearchParams();
        p.set('limit', '50');
        if (beforeId) p.set('before_id', beforeId);
        Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
        return p.toString();
    };

    const load = useCallback(async (beforeId) => {
        try {
            setError('');
            const data = await request.get(`${baseUrl()}/audit?${buildQuery(beforeId)}`);
            setRows(prev => beforeId ? [...prev, ...data.rows] : data.rows);
            setNextBefore(data.next_before_id);
        } catch (err) {
            setError(err.message || 'Грешка при зареждане на журнала.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        load(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const setFilter = (k, v) => setFilters(prev => ({...prev, [k]: v}));

    // Визуално групиране на СЪСЕДНИ еднакви действия (напр. 200× качване от един
    // файл) — редовете в базата си остават отделни (§5.3).
    const grouped = [];
    for (const r of rows) {
        const last = grouped[grouped.length - 1];
        if (last && last.action === r.action && last.username === r.username
            && last.settlement_ekatte === r.settlement_ekatte) {
            last.count += 1;
        } else {
            grouped.push({...r, count: 1});
        }
    }

    return (
        <div>
            <h3 className="mb-3">Активност</h3>

            <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                    <input className="form-control form-control-sm" placeholder="Потребител"
                           value={filters.username} onChange={e => setFilter('username', e.target.value)}/>
                </div>
                <div className="col-6 col-md-3">
                    <select className="form-select form-select-sm" value={filters.action}
                            onChange={e => setFilter('action', e.target.value)}>
                        <option value="">— всички действия —</option>
                        {Object.entries(ACTION_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                </div>
                <div className="col-6 col-md-3">
                    <input className="form-control form-control-sm" placeholder="Землище (ЕКАТТЕ)"
                           value={filters.settlement_ekatte}
                           onChange={e => setFilter('settlement_ekatte', e.target.value)}/>
                </div>
                <div className="col-6 col-md-3">
                    <input className="form-control form-control-sm" placeholder="Година"
                           value={filters.year} onChange={e => setFilter('year', e.target.value)}/>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <table className="table table-sm table-hover align-middle">
                <thead>
                <tr>
                    <th>Кога</th>
                    <th>Потребител</th>
                    <th>Действие</th>
                    <th>Детайли</th>
                    <th>Контекст</th>
                </tr>
                </thead>
                <tbody>
                {grouped.map((r, i) => (
                    <tr key={r.id + '-' + i}>
                        <td className="text-nowrap">{r.create_on}</td>
                        <td>{r.username}</td>
                        <td>
                            {label(r.action)}
                            {r.count > 1 && <span className="badge bg-secondary ms-1">×{r.count}</span>}
                        </td>
                        <td>{r.details || '—'}</td>
                        <td className="text-nowrap text-muted small">
                            {r.settlement_ekatte ? `землище ${r.settlement_ekatte}` : ''}
                            {r.year ? `${r.settlement_ekatte ? ', ' : ''}${r.year} г.` : ''}
                        </td>
                    </tr>
                ))}
                {rows.length === 0 && !error && (
                    <tr><td colSpan={5} className="text-muted">Няма записи.</td></tr>
                )}
                </tbody>
            </table>

            {nextBefore && (
                <button className="btn btn-outline-secondary" onClick={() => load(nextBefore)}>
                    Зареди още
                </button>
            )}
        </div>
    );
};
